import {
  DEFAULT_URL_CONTEXT_DENYLIST,
  DEFAULT_URL_CONTEXT_EXCLUDED_DOMAINS,
  DEFAULT_URL_CONTEXT_EXCLUDE_SELECTORS,
  SID_EXCLUDE_SELECTOR,
} from './constants';

export function normalizeParamKey(key: string): string {
  return key.trim().toLowerCase();
}

/** decodeURIComponent seguro — URI malformada não quebra o collector. */
export function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function buildDenylistSet(
  userDenylist: readonly string[] | undefined,
  mergeDefault: boolean | undefined
): Set<string> {
  const set = new Set<string>();
  if (mergeDefault !== false) {
    for (const key of DEFAULT_URL_CONTEXT_DENYLIST) {
      set.add(normalizeParamKey(key));
    }
  }
  for (const key of userDenylist ?? []) {
    if (typeof key === 'string' && key.trim()) {
      set.add(normalizeParamKey(key));
    }
  }
  return set;
}

export function buildExcludedDomains(
  userExcluded: readonly string[] | undefined,
  mergeDefault: boolean | undefined
): string[] {
  const list: string[] = [];
  if (mergeDefault !== false) {
    list.push(...DEFAULT_URL_CONTEXT_EXCLUDED_DOMAINS);
  }
  for (const domain of userExcluded ?? []) {
    if (typeof domain === 'string' && domain.trim()) {
      list.push(domain.trim().toLowerCase());
    }
  }
  return Array.from(new Set(list.map(d => d.toLowerCase())));
}

export function buildExcludeSelectors(
  userSelectors: readonly string[] | undefined,
  mergeDefault: boolean | undefined,
  includeSidSelector: boolean
): string[] {
  const list: string[] = [];
  if (mergeDefault !== false) {
    list.push(...DEFAULT_URL_CONTEXT_EXCLUDE_SELECTORS);
  }
  if (includeSidSelector) {
    list.push(SID_EXCLUDE_SELECTOR);
  }
  for (const sel of userSelectors ?? []) {
    if (typeof sel === 'string' && sel.trim()) {
      list.push(sel.trim());
    }
  }
  return Array.from(new Set(list));
}

export function isDeniedKey(key: string, denylist: Set<string>): boolean {
  return denylist.has(normalizeParamKey(key));
}

export function hostMatchesDomain(hostname: string, domain: string): boolean {
  const host = hostname.toLowerCase().replace(/\.$/, '');
  // remove porta se vier colada por engano
  const hostNoPort = host.replace(/:\d+$/, '');
  const target = domain.toLowerCase().replace(/^\./, '').replace(/:\d+$/, '');
  return hostNoPort === target || hostNoPort.endsWith(`.${target}`);
}

export function isDomainAllowed(
  hostname: string,
  allowedDomains: '*' | string[] | undefined,
  excludedDomains: readonly string[]
): boolean {
  /**
   * Hostname vazio (ex.: `file://` + link relativo / same-origin local).
   * Permitido apenas com sentinel `local` ou allow-all `*`.
   */
  if (!hostname) {
    if (allowedDomains === undefined || allowedDomains === '*') return true;
    return allowedDomains.some(
      domain => domain.trim().toLowerCase() === 'local'
    );
  }

  for (const excluded of excludedDomains) {
    if (hostMatchesDomain(hostname, excluded)) return false;
  }

  if (allowedDomains === undefined || allowedDomains === '*') return true;

  return allowedDomains.some(domain => {
    const normalized = domain.trim().toLowerCase();
    // Sentinel `local` não é hostname real
    if (normalized === 'local') return false;
    return hostMatchesDomain(hostname, domain);
  });
}

/** Gera ID público: `sid_<ts_base36>_<random>` — sem PII / session / visitor. */
export function generatePublicId(prefix = 'sid'): string {
  const safePrefix = (prefix || 'sid').replace(/[^a-zA-Z0-9_-]/g, '') || 'sid';
  const ts = Date.now().toString(36);
  const random = Math.random().toString(36).slice(2, 10);
  return `${safePrefix}_${ts}_${random}`;
}

export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * replaceState silencioso — usa History.prototype para não disparar
 * NavigationObserver / SPA PAGE_VIEW do Core.
 */
export function silentReplaceState(url: string): void {
  if (!isBrowser()) return;
  try {
    History.prototype.replaceState.call(
      history,
      history.state,
      document.title,
      url
    );
  } catch {
    // ignore — SSR / environments sem History
  }
}

/**
 * Parse de query string.
 * - duplicados: último valor vence (payload simples / idempotente)
 * - encoding malformado: não lança
 * - `+` → espaço
 */
export function parseSearchParams(search: string): Record<string, string> {
  const result: Record<string, string> = {};
  if (!search || search === '?') return result;

  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (!raw) return result;

  for (const part of raw.split('&')) {
    if (!part) continue;
    const eq = part.indexOf('=');
    const key = safeDecodeURIComponent(
      (eq >= 0 ? part.slice(0, eq) : part).replace(/\+/g, ' ')
    ).trim();
    if (!key) continue;
    const value =
      eq >= 0
        ? safeDecodeURIComponent(part.slice(eq + 1).replace(/\+/g, ' '))
        : '';
    result[key] = value;
  }

  return result;
}

export function buildSearchString(params: Record<string, string>): string {
  const pairs: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (!key) continue;
    pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(value ?? '')}`);
  }
  return pairs.length > 0 ? `?${pairs.join('&')}` : '';
}

/** Cópia rasa congelada — evita mutação externa do snapshot. */
export function freezeRecord(
  record: Record<string, string>
): Readonly<Record<string, string>> {
  return Object.freeze({ ...record });
}

/**
 * Reformata href decorado preservando forma relativa quando possível.
 * Garante `path?query#hash` (nunca `path#hash?query`).
 */
export function formatDecoratedHref(originalHref: string, url: URL): string {
  const trimmed = originalHref.trim();
  const search = url.search || '';
  const hash = url.hash || '';

  if (trimmed.startsWith('//')) {
    return `//${url.host}${url.pathname}${search}${hash}`;
  }

  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed)) {
    // absolute com scheme (http, https, ...)
    return url.toString();
  }

  if (trimmed.startsWith('/')) {
    return `${url.pathname}${search}${hash}`;
  }

  if (trimmed.startsWith('#')) {
    return `${search}${hash}`;
  }

  // relativo: checkout, ./checkout, ../checkout
  const pathPart = trimmed.split(/[?#]/)[0] ?? trimmed;
  return `${pathPart}${search}${hash}`;
}
